import React, { useEffect, useState } from "react";
import { Loader2, UserPlus, Trash2, ShieldCheck, User as UserIcon, Wrench, Eye } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { dashApi } from "../api";
import { useDashAuth } from "../AuthContext";
import { formatRelative, ROLE_LABEL, ROLES_INVITE } from "../utils";

const ROLE_ICON = {
  admin:      <ShieldCheck className="inline ml-2 h-3.5 w-3.5 text-emerald-600" title="Administrator" />,
  operations: <Wrench className="inline ml-2 h-3.5 w-3.5 text-violet-600" title="Operations" />,
  sales_rep:  <UserIcon className="inline ml-2 h-3.5 w-3.5 text-slate-500" title="Sales" />,
  read_only:  <Eye className="inline ml-2 h-3.5 w-3.5 text-slate-400" title="Read-only" />,
};

export default function UsersPage() {
  const { user } = useDashAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "sales_rep", initial_password: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await dashApi.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(e.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onInvite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await dashApi.post("/users", form);
      toast.success(`Invited ${form.email}`);
      setOpen(false);
      setForm({ email: "", name: "", role: "sales_rep", initial_password: "" });
      await load();
    } catch (er) {
      toast.error(er.message || "Invite failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (u) => {
    if (u.id === user.id) {
      toast.error("You cannot remove yourself.");
      return;
    }
    if (!window.confirm(`Remove ${u.email}? They will lose dashboard access immediately.`)) return;
    try {
      await dashApi.delete(`/users/${u.id}`);
      toast.success(`Removed ${u.email}`);
      await load();
    } catch (er) {
      toast.error(er.message || "Remove failed");
    }
  };

  const onChangeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await dashApi.patch(`/users/${u.id}`, { role });
      toast.success(`Role updated for ${u.email}`);
      await load();
    } catch (er) {
      toast.error(er.message || "Failed to update role");
    }
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Team</h1>
          <p className="text-sm text-slate-500 mt-1">
            {users.length} of 10 seats used.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-open-btn" disabled={users.length >= 10}>
              <UserPlus className="h-4 w-4 mr-2" /> Invite user
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite a new user</DialogTitle>
              <DialogDescription>
                They'll log in with the temp password you set here. Share it securely.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onInvite} className="space-y-3" data-testid="invite-user-form">
              <div>
                <Label htmlFor="invite-name">Name</Label>
                <Input
                  id="invite-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="invite-user-name-input"
                />
              </div>
              <div>
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="invite-user-email-input"
                />
              </div>
              <div>
                <Label htmlFor="invite-role">Role</Label>
                <select
                  id="invite-role"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                  data-testid="invite-user-role-select"
                >
                  {ROLES_INVITE.map((r) => (
                    <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="invite-password">Temporary password</Label>
                <Input
                  id="invite-password"
                  type="text"
                  required
                  minLength={8}
                  value={form.initial_password}
                  onChange={(e) => setForm({ ...form, initial_password: e.target.value })}
                  data-testid="invite-user-password-input"
                  placeholder="At least 8 characters"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} data-testid="invite-user-submit-btn">
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Invite
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-6 flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading users…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">User</th>
                  <th className="text-left font-medium px-4 py-2.5">Role</th>
                  <th className="text-left font-medium px-4 py-2.5 hidden md:table-cell">Last sign-in</th>
                  <th className="text-right font-medium px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{u.name}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role}
                        onChange={(e) => onChangeRole(u, e.target.value)}
                        disabled={u.id === user.id}
                        className="h-8 rounded-md border border-slate-200 px-2 text-xs bg-white"
                        data-testid={`user-role-select-${u.id}`}
                      >
                        {ROLES_INVITE.map((r) => (
                          <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                        ))}
                      </select>
                      {ROLE_ICON[u.role] || null}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-600 text-xs">
                      {u.last_login_at ? formatRelative(u.last_login_at) : "never"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {u.id !== user.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(u)}
                          className="text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                          data-testid={`user-remove-btn-${u.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                        </Button>
                      )}
                      {u.id === user.id && (
                        <span className="text-xs text-slate-400">You</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
